import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import {
  DIRECTED_MAX_CAST_PER_SHOT,
  type DirectedCastEntry,
  type DirectedShot,
} from 'fym-shared/src/music24/directedVideoApi';
import type { MusicVideoEffect } from 'fym-shared/src/music24/musicVideoEffects';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../music24/components/primitives/SMLButton';
import { effectKindMessages } from '../../../musicVideoEditor/messages/effectMessages';
import { formatTime } from '../../../musicVideoEditor/timeline/timelineGeometry';
import { scriptMessages as messages } from '../../messages/scriptMessages';
import { useDirectedCastNames } from '../../useDirectedCastNames';
import { EffectChip } from './EffectChip';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the script step; each part uses its own slice of it */
import styles from './Script.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import steps from '../DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import shell from '../../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface ShotRewriteControls {
  note: string;
  onNote: (note: string) => void;
  onRewrite: () => void;
  disabled: boolean;
}

interface ShotEditorProps {
  shot: DirectedShot;
  cast: readonly DirectedCastEntry[];
  effects: readonly MusicVideoEffect[];
  isSaving: boolean;
  /** "Rewrite this shot" with a note; null for a new shot (the director never saw it). */
  rewrite: ShotRewriteControls | null;
  onChange: (shot: DirectedShot) => void;
  onSave: () => void;
  onCancel: () => void;
}

/**
 * The fields of one shot (design e1, shot 3): what happens, the camera, the cast of the shot (at
 * most 3), the prompt for the AI, its effects, Save and Cancel, and "Rewrite this shot" with a note.
 */
const ShotEditorComponent: React.FC<ShotEditorProps> = ({
  shot,
  cast,
  effects,
  isSaving,
  rewrite,
  onChange,
  onSave,
  onCancel,
}) => {
  const intl = useIntl();
  const { named, toIds } = useDirectedCastNames();
  const isFull = shot.castIds.length >= DIRECTED_MAX_CAST_PER_SHOT;
  const sceneEmpty = shot.scene.trim() === '';
  const toggleCast = (castId: string) =>
    onChange({
      ...shot,
      castIds: shot.castIds.includes(castId) ? shot.castIds.filter((id) => id !== castId) : [...shot.castIds, castId],
    });

  return (
    <div className={styles.editor}>
      <label className={styles.editorLabel} htmlFor={`shot-scene-${shot.id}`}>
        {intl.formatMessage(messages.whatHappens)}
      </label>
      <textarea
        id={`shot-scene-${shot.id}`}
        className={styles.input}
        rows={3}
        value={named(shot.scene)}
        onChange={(event) => onChange({ ...shot, scene: toIds(event.target.value) })}
      />
      <div className={styles.editorRow}>
        <div className={styles.editorColumn}>
          <label className={styles.editorLabel} htmlFor={`shot-camera-${shot.id}`}>
            {intl.formatMessage(messages.camera)}
          </label>
          <input
            id={`shot-camera-${shot.id}`}
            className={styles.input}
            value={shot.camera}
            onChange={(event) => onChange({ ...shot, camera: event.target.value })}
          />
        </div>
        {cast.length > 0 && (
          <div className={clsx(styles.editorColumn, styles.editorColumnWide)}>
            <span className={styles.editorLabel}>
              {intl.formatMessage(messages.castInShot, {
                count: shot.castIds.length,
                max: DIRECTED_MAX_CAST_PER_SHOT,
              })}
            </span>
            <div className={styles.castPicker}>
              {cast.map((entry) => {
                const isOn = shot.castIds.includes(entry.id);
                return (
                  <button
                    key={entry.id}
                    type="button"
                    aria-pressed={isOn}
                    className={clsx(styles.castPick, isOn && styles.castPickOn)}
                    disabled={!isOn && isFull}
                    onClick={() => toggleCast(entry.id)}
                  >
                    {entry.url === '' ? <i aria-hidden /> : <img src={entry.url} alt="" />}
                    {entry.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <label className={styles.editorLabel} htmlFor={`shot-prompt-${shot.id}`}>
        {intl.formatMessage(messages.prompt)}
      </label>
      <textarea
        id={`shot-prompt-${shot.id}`}
        className={clsx(styles.input, styles.inputMono)}
        rows={3}
        value={named(shot.veoPrompt)}
        onChange={(event) => onChange({ ...shot, veoPrompt: toIds(event.target.value) })}
      />
      {effects.length > 0 && (
        <div className={styles.editorLine}>
          <span className={styles.editorLabel}>{intl.formatMessage(messages.effects)}</span>
          {effects.map((effect) => (
            <EffectChip
              key={effect.id}
              effect={effect}
              label={intl.formatMessage(messages.effectChip, {
                name: intl.formatMessage(effectKindMessages[effect.kind]),
                from: formatTime(effect.startSec),
                to: formatTime(effect.endSec),
              })}
            />
          ))}
        </div>
      )}
      {sceneEmpty && <p className={styles.cameraLine}>{intl.formatMessage(messages.emptyScene)}</p>}
      <div className={styles.editorLine}>
        <SMLButton variant="primary" className={steps.small} disabled={isSaving || sceneEmpty} onClick={onSave}>
          {intl.formatMessage(messages.saveShot)}
        </SMLButton>
        <SMLButton className={clsx(steps.small, steps.ghost)} disabled={isSaving} onClick={onCancel}>
          {intl.formatMessage(messages.cancel)}
        </SMLButton>
        <span className={shell.grow} />
        {rewrite !== null && (
          <span className={styles.noteBox}>
            <input
              className={styles.input}
              value={rewrite.note}
              placeholder={intl.formatMessage(messages.shotNote)}
              aria-label={intl.formatMessage(messages.shotNote)}
              onChange={(event) => rewrite.onNote(event.target.value)}
            />
            <SMLButton
              className={clsx(steps.small, styles.noteButton)}
              disabled={rewrite.disabled}
              onClick={rewrite.onRewrite}
            >
              {intl.formatMessage(messages.rewriteShot)}
            </SMLButton>
          </span>
        )}
      </div>
    </div>
  );
};

export const ShotEditor = TypedMemo(ShotEditorComponent);
